import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { supabase } from './supabaseClient'
import { isNativeApp } from './platform'
import { SUPPORT_ANDROID_CHANNEL, SUPPORT_PUSH_VERSION } from './supportNotifications'
import { createSupportClientId } from './supportApi'

const nativePlatform = () => isNativeApp() && ['ios','android'].includes(Capacitor.getPlatform()) ? Capacitor.getPlatform() : null
export const SUPPORT_REGISTRATION_TIMEOUT_MS = 12000
let supportRegistrationQueue = Promise.resolve()
const serializeSupportRegistration = operation => {
  const result = supportRegistrationQueue.then(operation,operation)
  supportRegistrationQueue = result.then(()=>{},()=>{})
  return result
}
async function readSession() {
  let timer
  try {
    return await Promise.race([supabase.auth.getSession(),new Promise((_,reject)=>{timer=setTimeout(()=>reject(new Error('session_timeout')),3000)})])
  } finally {clearTimeout(timer)}
}
async function invokeSupportRegistration(options) {
  const controller=new AbortController()
  let timer
  try {
    return await Promise.race([
      supabase.functions.invoke('register-support-push-token',{...options,signal:controller.signal}),
      new Promise((_,reject)=>{timer=setTimeout(()=>{controller.abort();reject(new Error('registration_timeout'))},SUPPORT_REGISTRATION_TIMEOUT_MS)}),
    ])
  } finally {clearTimeout(timer)}
}

export async function registerPushNotifications({ onNotification } = {}) {
  const platform = nativePlatform()
  if (!platform) return () => {}
  const handles = []
  let active = true, registeredToken = null, ticketReady = false
  let initialAuth,initialError
  try {const result=await readSession();initialAuth=result.data;initialError=result.error}catch{return () => {}}
  const owner = initialAuth?.session?.user?.id
  let ownerToken = initialAuth?.session?.access_token
  if (initialError || !owner || !ownerToken) return () => {}
  let authSubscription
  try {
    authSubscription=supabase.auth.onAuthStateChange?.((event,session)=>{
      // This callback only updates memory; do not call Auth/RPC methods while
      // Supabase holds its Auth notification lock.
      if(session?.user?.id===owner&&session.access_token)ownerToken=session.access_token
    })?.data?.subscription
  } catch {}
  let registrationId
  try {registrationId=createSupportClientId()}catch{/* Keep legacy iOS delivery when capability opt-in is unavailable. */}
  const supportRegistration = (token,action='register') => serializeSupportRegistration(async()=>{
    if (!registrationId || (action!=='disable'&&!active) || (action==='register'&&!ticketReady)) return {error:null,data:{ok:false}}
    let current
    try {current=await readSession()}catch{}
    const session=current?.data?.session
    if (session?.user?.id===owner && session.access_token) ownerToken=session.access_token
    else if (action!=='disable') return {error:null,data:{ok:false}}
    if (action!=='disable'&&!active) return {error:null,data:{ok:false}}
    // Cleanup uses only the last token known to belong to this owner. A newer
    // account's token can never disable the previous owner's device capability.
    // A timeout is ambiguous: the server-issued ticket ordering/CAS protects any
    // later generation even if this older request commits after the queue moves.
    return invokeSupportRegistration({headers:{Authorization:`Bearer ${ownerToken}`},body:{...(token?{token}:{}),platform,action,supportVersion:SUPPORT_PUSH_VERSION,registrationId}})
  })
  // Ticket preparation runs behind the UI and listener installation. A cold
  // notification tap need not wait for this extra network request.
  const preparation=supportRegistration(null,'prepare').then(prepared=>{
    ticketReady=prepared?.data?.ok===true&&!prepared.error
  }).catch(()=>{console.warn('Unable to prepare Support Hubbing notifications')})

  try {
    handles.push(await PushNotifications.addListener('registration', async token => {
      try {
        if (!active || !token?.value) return
        const {data,error} = await readSession()
        if (!active || error || data?.session?.user?.id !== owner) return
        if (data.session.access_token) ownerToken=data.session.access_token
        registeredToken=token.value
        // Legacy delivery is APNs-only; never put an FCM token in its registry.
        if (platform==='ios') {
          const result=await supabase.functions.invoke('register-push-token',{headers:{Authorization:`Bearer ${ownerToken}`},body:{token:token.value,platform}})
          if (result.error) console.warn('Unable to save push token')
        }
        if (!active) return
        await preparation
        if (!active) return
        const result=await supportRegistration(token.value)
        if (result.error) console.warn('Unable to register Support Hubbing notifications')
        if (!active) await supportRegistration(token.value,'disable')
      } catch {console.warn('Unable to register push notifications')}
    }))
    handles.push(await PushNotifications.addListener('registrationError',()=>console.warn('Push notification registration failed')))
    handles.push(await PushNotifications.addListener('pushNotificationReceived',notification=>{if(active)onNotification?.(notification,{action:'received'})}))
    handles.push(await PushNotifications.addListener('pushNotificationActionPerformed',action=>{if(active)onNotification?.(action?.notification,{action:'performed'})}))
    if (platform==='android') await PushNotifications.createChannel({id:SUPPORT_ANDROID_CHANNEL,name:'Support Hubbing',description:'Messages officiels du Support Hubbing',importance:4,visibility:0,sound:'default'})
    let permission=await PushNotifications.checkPermissions()
    if (['prompt','prompt-with-rationale'].includes(permission.receive)) permission=await PushNotifications.requestPermissions()
    if (permission.receive==='granted') await PushNotifications.register()
  } catch {console.warn('Push notifications unavailable')}

  return () => {
    active=false
    authSubscription?.unsubscribe()
    handles.forEach(handle=>{try{Promise.resolve(handle?.remove?.()).catch(()=>{})}catch{}})
    if (registeredToken) supportRegistration(registeredToken,'disable').catch(()=>{})
  }
}
